import React, { useCallback, useEffect, useMemo, useState } from "react";

import { useQueryClient } from "@tanstack/react-query";
import { groupBy, orderBy } from "lodash";
import { useSelector } from "react-redux";

import useGetAssetClasses from "@fartherfinance/frontend/api/Investment/hooks/useGetAssetClasses";
import useGetPortfolioPlans, {
  portfolioPlanKeyMaker,
} from "@fartherfinance/frontend/api/Investment/hooks/useGetPortfolioPlans";
import { AssetClassId } from "@fartherfinance/frontend/api/Investment/types";
import useSecuritySearchByTicker, {
  getSecuritySearchByTickerKeyMaker,
} from "@fartherfinance/frontend/api/SecurityMaster/hooks/useSecuritySearchByTicker";
import { PortfolioPlanId } from "@fartherfinance/frontend/api/Types";

import { EntityOptions } from "../constants";
import transformGetAssetClassesData from "../helpers/transformGetAssetClassesData";
import {
  ADD_ASSET_CLASS_TO_MODEL,
  ADD_CUSTOM_ASSET_CLASS_TO_LIST,
  ADD_SECURITY_TO_ASSET_CLASS_IN_MODEL,
  ADD_SLEEVE_TO_ASSET_CLASS_IN_MODEL,
  SET_DEFAULT_ASSET_CLASSES_AND_THEIR_CATEGORIES,
} from "../reducer/actions";
import { usePortfolioBuilderContext } from "../reducer/Context";
import {
  isSleeveAlreadyInModel,
  NO_ASSET_CLASS_ASSET_CLASS,
} from "../reducer/reducer";
import {
  AssetCategoryAndClass,
  AssetCategoryNotCashAndClass,
  AssetClassCategory,
  AssetClassDropdownOption,
  ElementID,
  EntityOption,
  SecurityDropdownOption,
  SleeveDropdownOption,
} from "../reducer/Types";
import { isTickerIdentifier } from "@src/multiCustodian/components/Admin/EquivalentSecurities/utils";
import useAdvisorRequestAuth from "@src/multiCustodian/hooks/useAdvisorRequestAuth";
import useStatusNotification from "@src/multiCustodian/hooks/useStatusNotification";
import useWindowDimensions from "@src/multiCustodian/hooks/useWindowDimensions";
import CustomDropdownPopoverBase from "@src/sharedComponents/CustomDropdownBase/CustomDropdownPopoverBase";
import MenuOptionsWithCheckIfSelected, {
  CategoryWithOptions,
  DropdownMenuOption,
} from "@src/sharedComponents/CustomDropdownBase/MenuOptionsWithCheckIfSelected/MenuOptionsWithCheckIfSelected";
import BaseDropdown from "@src/sharedComponents/Dropdown/BaseDropdown";
import SearchInput from "@src/sharedComponents/SearchInput/SearchInput";
import { State } from "@src/store";

import AddToModelButton from "./AddToModelButton";
import CreateCustomAssetClassDropdownOption from "./CreateCustomAssetClassDropdownOption/CreateCustomAssetClassDropdownOption";
import CreateAssetClassModal from "./CreateEditAssetClassModal/CreateEditAssetClassModal";
import TableAndSearchBarHeader from "./TableAndSearchBarHeader/TableAndSearchBarHeader";

import styles from "./SearchBarAndTableConstructor.module.css";

const SEARCH_INPUT_WIDTH = 354;
const SEARCH_INPUT_MIN_WIDTH = 250;
const SEARCH_INPUT_MENU_HEIGHT = 440;

type DMO = DropdownMenuOption<AssetClassId>;
type CWO = CategoryWithOptions<AssetClassCategory, DMO>;

interface Props {
  portfolioPlanId: PortfolioPlanId | null;
}

const SearchBarAndTableConstructor: React.FC<Props> = ({ portfolioPlanId }) => {
  const { advisorId } = useSelector((state: State) => ({
    advisorId: state.main_Reducer.cockroach_advisor_id,
  }));

  const [showCreateAssetClassModal, setShowCreateAssetClassModal] =
    useState<boolean>(false);

  // Chosen entity to search (via the search bar) for/add to table/model
  const [entity, setEntity] = useState<EntityOption>(EntityOptions[0]);

  // Input for the search bar
  const [searchInput, setSearchInput] = useState<string>("");

  // Shows/hides the popover dropdown below the search bar
  const [showSearchInputDropdown, setShowSearchInputDropdown] =
    useState<boolean>(false);

  const { windowWidth } = useWindowDimensions();

  const viewportWidth = useMemo(() => windowWidth, [windowWidth]);

  const SEARCH_DROPDOWN_WIDTH = useMemo(
    () => (viewportWidth >= 1270 ? SEARCH_INPUT_WIDTH : SEARCH_INPUT_MIN_WIDTH),
    [viewportWidth]
  );

  const notify = useStatusNotification();

  const queryClient = useQueryClient();

  const { state, dispatch } = usePortfolioBuilderContext();

  const addCustomAssetClassToBaseList = useCallback(
    (assetCategoryNotCashWithNewCustomClass: AssetCategoryNotCashAndClass) => {
      dispatch({
        type: ADD_CUSTOM_ASSET_CLASS_TO_LIST,
        payload: assetCategoryNotCashWithNewCustomClass,
      });

      setShowCreateAssetClassModal(false);
    },
    [dispatch, setShowCreateAssetClassModal]
  );

  const [assetClassToAssign, setAssetClassToAssign] = useState<
    AssetClassDropdownOption | undefined
  >(undefined);

  useEffect(() => {
    // if the assetClassToAssign is removed from the table and the assetCategoriesWithClasses list, remove it from being set in state
    if (
      assetClassToAssign !== undefined &&
      state.modelTable.every((ac) => ac.id !== assetClassToAssign.id)
    ) {
      setAssetClassToAssign(undefined);
    }

    // don't care about if assetClassToAssign changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.modelTable]);

  const [selectedAssetClass, setSelectedAssetClass] = useState<
    AssetCategoryAndClass | undefined
  >(undefined);

  const [selectedSleeve, setSelectedSleeve] = useState<
    SleeveDropdownOption | undefined
  >(undefined);

  const [selectedSecurity, setSelectedSecurity] = useState<
    SecurityDropdownOption | undefined
  >(undefined);

  const [inputForSearchRequest, setInputForSearchRequest] = useState<
    string | null
  >(null);

  const [debounceTimeout, setDebounceTimeout] = useState<NodeJS.Timeout | null>(
    null
  );

  const auth = useAdvisorRequestAuth();

  const assetClassesAndTheirCategories = useGetAssetClasses(advisorId, auth);

  useEffect(() => {
    if (assetClassesAndTheirCategories.data !== undefined) {
      dispatch({
        type: SET_DEFAULT_ASSET_CLASSES_AND_THEIR_CATEGORIES,
        payload: transformGetAssetClassesData(
          assetClassesAndTheirCategories.data.data
        ),
      });
    }
  }, [assetClassesAndTheirCategories.data, dispatch]);

  const modelNameSearchStr =
    entity.id !== "Sleeves" ||
    inputForSearchRequest === null ||
    inputForSearchRequest.length < 1
      ? undefined
      : inputForSearchRequest;

  const portfolioPlanParams = useMemo(
    () => ({ search: modelNameSearchStr, pageSize: "50" }),
    [modelNameSearchStr]
  );

  const portfolios = useGetPortfolioPlans(
    portfolioPlanParams,
    modelNameSearchStr ? auth : null
  );

  const portfolioSearchResults = useMemo(
    () =>
      orderBy(
        (portfolios.data?.data ?? []).map((p) => ({
          id: p.plan.portfolioPlanId,
          label: p.plan.name,
        })),
        [(p) => p.label.toLowerCase()],
        ["asc"]
      ),
    [portfolios.data]
  );

  const portfolioOptions = useMemo(() => {
    if (searchInput === "") {
      return [];
    }

    return portfolioSearchResults;
  }, [portfolioSearchResults, searchInput]);

  const validSearchInput =
    entity.id !== "Securities" ||
    inputForSearchRequest === null ||
    inputForSearchRequest.length < 1
      ? null
      : inputForSearchRequest;

  const securities = useSecuritySearchByTicker(validSearchInput, auth);

  const sleevesQueryKey = portfolioPlanKeyMaker(portfolioPlanParams);
  const isFetchingSleeves =
    modelNameSearchStr === "" || modelNameSearchStr === null
      ? false
      : queryClient.getQueryState(sleevesQueryKey)?.fetchStatus === "fetching";

  // if validSearchInput is null -> "" -> don't pass "" key into queryClient.getQueryState, just set isFetchingSecurity to false
  const securityQueryKey = getSecuritySearchByTickerKeyMaker(
    validSearchInput ?? ""
  );
  const isFetchingSecurity =
    validSearchInput === null
      ? false
      : queryClient.getQueryState(securityQueryKey)?.fetchStatus === "fetching";

  const securitySearchResults = useMemo(() => {
    // filter for active tickers
    const activeSecurities = (securities.data ?? []).filter(
      (res) =>
        res.state === "Active" &&
        res.identifiers.some((idr) => idr.registrar === "TICKER")
    );

    const securityOptions = activeSecurities.map((res) => {
      const tickerIdentifier = res.identifiers.find(
        (idr) => idr.registrar === "TICKER"
      );
      if (
        tickerIdentifier === undefined ||
        !isTickerIdentifier(tickerIdentifier)
      ) {
        throw new Error(
          `No ticker identifier for: ${res.name ?? "No Name Available"}, id: ${
            res.id
          }`
        );
      }

      return {
        id: tickerIdentifier.securityId,
        label: tickerIdentifier.identifier,
        label2: tickerIdentifier.description ?? "(no description)",
      };
    });

    return orderBy(securityOptions, [(s) => s.label.toLowerCase()], ["asc"]);
  }, [securities.data]);

  const securityOptions = useMemo(() => {
    if (searchInput === "") {
      return [];
    }

    return securitySearchResults;
  }, [securitySearchResults, searchInput]);

  // Don't want to make a fetch after each key press, so debounce the input change/key press
  const debounceSetInput = useCallback(
    (searchTerm: string) => {
      if (debounceTimeout !== null) {
        clearTimeout(debounceTimeout);
      }

      if (searchTerm) {
        const debTO = setTimeout(() => {
          setInputForSearchRequest(searchTerm);
          setShowSearchInputDropdown(true);
        }, 1200);

        setDebounceTimeout(debTO);
      }
    },
    [debounceTimeout]
  );

  const assetCategoriesWithClassesFiltered = useMemo(() => {
    if (searchInput === "") {
      // a reset: make a deep copy of the base list of assetCategoriesWithClasses (that can be added to) and set the filtered list to the base list
      return state.assetCategoriesWithClasses.map((acc) => ({
        category: acc.category,
        options: [...acc.options],
      }));
    } else {
      // filter base list
      const searchTerm = searchInput.toLowerCase();

      // first filter by category
      const filteredAssetClasses = state.assetCategoriesWithClasses.filter(
        (acc) =>
          acc.options.some((ac) => ac.label.toLowerCase().includes(searchTerm))
      );

      // second filter the classes of the already filtered categories
      return filteredAssetClasses.map((acc) => {
        return {
          category: acc.category,
          options: acc.options.filter((ac) =>
            ac.label.toLowerCase().includes(searchTerm)
          ),
        };
      });
    }
  }, [searchInput, state.assetCategoriesWithClasses]);

  const assetClassesInModel = useMemo(() => {
    const groupedByCategory = groupBy(state.modelTable, (ac) => ac.category);

    return orderBy(
      Object.entries(groupedByCategory).map(([cat, assetClasses]) => ({
        category: cat,
        options: orderBy(assetClasses, [(ac) => ac.name], ["asc"]).map(
          (ac) => ({
            id: ac.id,
            label: ac.name,
            label2: ac.isInvisible
              ? ""
              : (assetClassesAndTheirCategories.data?.data ?? []).find(
                  (el) => el.assetClassId === ac.assetClassId
                )?.ownerId !== null
              ? "(Custom)"
              : "",
          })
        ),
      })),
      [(cat) => cat.category],
      ["asc"]
    );
  }, [state.modelTable, assetClassesAndTheirCategories.data]);

  const selectedAssetClassIsAlreadyInModel = useMemo(() => {
    if (selectedAssetClass === undefined) {
      return false;
    }

    return state.modelTable.some(
      (ac) => ac.name === selectedAssetClass.assetClassName
    );
  }, [selectedAssetClass, state.modelTable]);

  const isAddToModelButtonDisabled = useMemo(() => {
    if (selectedAssetClassIsAlreadyInModel) {
      return true;
    }

    if (entity.id === "Asset Classes" && selectedAssetClass === undefined) {
      return true;
    }

    if (entity.id === "Sleeves" && selectedSleeve === undefined) {
      return true;
    }

    if (entity.id === "Securities" && selectedSecurity === undefined) {
      return true;
    }

    return false;
  }, [
    entity.id,
    selectedAssetClass,
    selectedSleeve,
    selectedSecurity,
    selectedAssetClassIsAlreadyInModel,
  ]);

  const onHideCreateAssetClassModal = () => setShowCreateAssetClassModal(false);

  const onHideSearchInputDropdown = () => setShowSearchInputDropdown(false);

  const onShowSearchInputDropdown = () => {
    if (entity.id === "Asset Classes") {
      setShowSearchInputDropdown(true);
    }

    if (
      searchInput !== "" &&
      (entity.id === "Securities" || entity.id === "Sleeves")
    ) {
      setShowSearchInputDropdown(true);
    }
  };

  const onSelectEntity = (entityOption: EntityOption) => {
    setEntity(entityOption);
    setSearchInput("");
    setSelectedAssetClass(undefined);
  };

  const onSelectAssetClass = (
    ac: DropdownMenuOption<AssetClassId>,
    cat?: AssetClassCategory
  ) => {
    if (cat === undefined) {
      throw new Error(
        "Category should not be undefined in onSelect in MenuOptionsWithCheckIfSelected if categoryWithOptions in not undefined"
      );
    }

    setSelectedAssetClass({
      assetClassId: ac.id,
      assetClassName: ac.label,
      category: cat,
    });
    setSearchInput(ac.label);
    setShowSearchInputDropdown(false);
  };

  const onSelectAssetClassToAssign = (ac: DropdownMenuOption<ElementID>) =>
    setAssetClassToAssign(ac);

  const onAddToModelTable = useCallback(() => {
    if (entity.id === "Asset Classes") {
      if (selectedAssetClass === undefined) {
        return;
      }

      dispatch({
        type: ADD_ASSET_CLASS_TO_MODEL,
        payload: selectedAssetClass,
      });
      setSelectedAssetClass(undefined);
      setSearchInput("");
    } else if (entity.id === "Securities") {
      if (selectedSecurity === undefined) {
        return;
      }

      const assignedAssetClass =
        assetClassToAssign === undefined
          ? NO_ASSET_CLASS_ASSET_CLASS.id
          : assetClassToAssign.id;

      dispatch({
        type: ADD_SECURITY_TO_ASSET_CLASS_IN_MODEL,
        payload: {
          ticker: selectedSecurity.label,
          securityId: selectedSecurity.id,
          description: selectedSecurity.label2,
          assetClassElementId: assignedAssetClass,
        },
      });
      setSelectedSecurity(undefined);
      setSearchInput("");
    } else if (entity.id === "Sleeves") {
      if (selectedSleeve === undefined) {
        return;
      }

      if (
        (portfolioPlanId !== null && selectedSleeve.id === portfolioPlanId) ||
        isSleeveAlreadyInModel(selectedSleeve.id, state.modelTable)
      ) {
        notify("Portfolio is already in this model", "Error");
        return;
      }

      const assignedAssetClass =
        assetClassToAssign === undefined
          ? NO_ASSET_CLASS_ASSET_CLASS.id
          : assetClassToAssign.id;

      dispatch({
        type: ADD_SLEEVE_TO_ASSET_CLASS_IN_MODEL,
        payload: {
          name: selectedSleeve.label,
          portfolioPlanId: selectedSleeve.id,
          assetClassElementId: assignedAssetClass,
        },
      });
      setSelectedSleeve(undefined);
      setSearchInput("");
    }
  }, [
    entity,
    selectedAssetClass,
    selectedSleeve,
    selectedSecurity,
    assetClassToAssign,
    dispatch,
    notify,
    state.modelTable,
    portfolioPlanId,
  ]);

  const onHandleSearchInput = useCallback(
    (input: string) => {
      if (entity.id === "Asset Classes") {
        setSelectedAssetClass(undefined);
        setSearchInput(input);
        debounceSetInput(input);
      } else if (entity.id === "Securities") {
        const searchTerm = input.trim().toUpperCase();
        setSelectedSecurity(undefined);
        setSearchInput(input.trim());
        debounceSetInput(searchTerm);
      } else if (entity.id === "Sleeves") {
        setSelectedSleeve(undefined);
        setSearchInput(input);
        debounceSetInput(input);
      }
    },
    [entity, debounceSetInput]
  );

  const onSelectSleeve = (sleeveOption: SleeveDropdownOption) => {
    setSelectedSleeve(sleeveOption);
    setSearchInput(sleeveOption.label);
    setShowSearchInputDropdown(false);
  };

  const onSelectSecurity = (securityOption: SecurityDropdownOption) => {
    setSelectedSecurity(securityOption);
    setSearchInput(securityOption.label);
    setShowSearchInputDropdown(false);
  };

  const shouldRenderTooltip = useCallback(
    (optionLabel: string) => {
      if (SEARCH_DROPDOWN_WIDTH === 354) {
        return optionLabel.length >= 45;
      }

      return optionLabel.length >= 30;
    },
    [SEARCH_DROPDOWN_WIDTH]
  );

  const shouldRenderTooltip2 = useCallback(
    (optionLabel: string) => {
      if (SEARCH_DROPDOWN_WIDTH === 354) {
        return optionLabel.length >= 26;
      }

      return optionLabel.length >= 14;
    },
    [SEARCH_DROPDOWN_WIDTH]
  );

  return (
    <>
      {showCreateAssetClassModal && (
        <CreateAssetClassModal
          title={"Create asset class"}
          isOpen={showCreateAssetClassModal}
          onClose={onHideCreateAssetClassModal}
          onSubmit={addCustomAssetClassToBaseList}
        />
      )}

      <div className={styles.container}>
        <div className={styles.topContainer}>
          <TableAndSearchBarHeader portfolioPlanId={portfolioPlanId}>
            <AddToModelButton
              className={styles.responsiveButtonInHeader}
              onClick={onAddToModelTable}
              disabled={isAddToModelButtonDisabled}
              buttonHeight={32}
            />
          </TableAndSearchBarHeader>
        </div>

        <div className={styles.bottomContainer}>
          <div className={styles.leftContainer}>
            <div className={styles.flexRow}>
              <BaseDropdown
                curItemLabel={entity.label}
                options={EntityOptions}
                onSelect={onSelectEntity}
                customDropdownBaseClassName={styles.entitySelectDropdown}
                optionsMenuClassName={styles.optionsMenu}
                width={170}
              />

              <CustomDropdownPopoverBase
                className={styles.dropdownMenu}
                popoverElement={
                  <SearchInput
                    searchInput={searchInput}
                    setSearchInput={onHandleSearchInput}
                    placeholder={
                      entity.id === "Asset Classes"
                        ? "Select asset class to add"
                        : "Search..."
                    }
                    hideSearchIcon={true}
                    className={styles.searchInputContainer}
                    inputClassName={styles.searchInput}
                    onClick={onShowSearchInputDropdown}
                  />
                }
                showDropdown={showSearchInputDropdown}
                closeDropdown={onHideSearchInputDropdown}
                menuWidth={SEARCH_DROPDOWN_WIDTH}
              >
                {entity.id === "Asset Classes" ? (
                  <>
                    <CreateCustomAssetClassDropdownOption
                      setShowSearchInputDropdown={setShowSearchInputDropdown}
                      setShowCreateAssetClassModal={
                        setShowCreateAssetClassModal
                      }
                    />

                    <MenuOptionsWithCheckIfSelected<
                      AssetClassId,
                      DMO,
                      AssetClassCategory,
                      CWO
                    >
                      curItemName={selectedAssetClass?.assetClassName}
                      onSelect={onSelectAssetClass}
                      options={undefined}
                      categoryWithOptions={assetCategoriesWithClassesFiltered}
                      displayLabel2InCategoryWithOptions={true}
                      width={SEARCH_DROPDOWN_WIDTH}
                      maxHeight={SEARCH_INPUT_MENU_HEIGHT}
                      isLoading={assetClassesAndTheirCategories.isLoading}
                      shouldRenderTooltip={shouldRenderTooltip}
                    />
                  </>
                ) : entity.id === "Securities" ? (
                  <MenuOptionsWithCheckIfSelected
                    curItemId={selectedSecurity?.id}
                    curItemName={selectedSecurity?.label}
                    onSelect={onSelectSecurity}
                    options={securityOptions}
                    categoryWithOptions={undefined}
                    width={SEARCH_DROPDOWN_WIDTH}
                    maxHeight={SEARCH_INPUT_MENU_HEIGHT}
                    isLoading={isFetchingSecurity}
                    shouldRenderTooltip={shouldRenderTooltip}
                  />
                ) : (
                  <MenuOptionsWithCheckIfSelected
                    curItemId={selectedSleeve?.id}
                    curItemName={selectedSleeve?.label}
                    onSelect={onSelectSleeve}
                    options={portfolioOptions}
                    categoryWithOptions={undefined}
                    width={SEARCH_DROPDOWN_WIDTH}
                    maxHeight={SEARCH_INPUT_MENU_HEIGHT}
                    isLoading={isFetchingSleeves}
                    shouldRenderTooltip={shouldRenderTooltip}
                  />
                )}
              </CustomDropdownPopoverBase>
            </div>

            <BaseDropdown
              placeholder={
                viewportWidth >= 1310
                  ? "Assign asset class (optional)"
                  : "Assign asset class"
              }
              curItemId={assetClassToAssign?.id}
              curItemLabel={assetClassToAssign?.label}
              options={undefined}
              categoryWithOptions={assetClassesInModel}
              displayLabel2InCategoryWithOptions={true}
              onSelect={onSelectAssetClassToAssign}
              width={viewportWidth >= 1310 ? 240 : 170}
              customDropdownBaseClassName={styles.assetClassToAssignDropdown}
              customDropdownBaseTextClassName={
                styles.assetClassToAssignDropdownMenuItemSelectedText
              }
              optionsMenuClassName={styles.optionsMenu}
              isDisabled={entity.id === "Asset Classes"}
              shouldRenderTooltip={shouldRenderTooltip2}
            />
          </div>

          <AddToModelButton
            className={styles.responsiveButtonInSearchBar}
            onClick={onAddToModelTable}
            disabled={isAddToModelButtonDisabled}
          />
        </div>
      </div>
    </>
  );
};

export default SearchBarAndTableConstructor;
